import React from 'react';
import { cn } from '../../../lib/utils';
import { Button } from '../../../components/ui/button';
import { Switch } from '../../../components/ui/switch';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../../../components/ui/table';
import { Edit, Trash2, Loader2 } from 'lucide-react';
import { PlansTableProps } from '../types';

export function PlansTable({
  isDark,
  plans,
  isLoading,
  onEdit,
  onDelete,
  onToggleActive,
  formatPrice,
  formatDuration,
}: PlansTableProps) {
  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="h-8 w-8 animate-spin text-[#0EA5A4]" />
      </div>
    );
  }

  return (
    <Table>
      <TableHeader>
        <TableRow className={cn(
          isDark ? 'border-slate-700 hover:bg-slate-800/50' : 'border-slate-100 hover:bg-slate-50'
        )}>
          <TableHead className={cn('font-semibold', isDark ? 'text-slate-400' : 'text-slate-500')}>
            Code
          </TableHead>
          <TableHead className={cn('font-semibold', isDark ? 'text-slate-400' : 'text-slate-500')}>
            Title
          </TableHead>
          <TableHead className={cn('font-semibold', isDark ? 'text-slate-400' : 'text-slate-500')}>
            Duration
          </TableHead>
          <TableHead className={cn('font-semibold', isDark ? 'text-slate-400' : 'text-slate-500')}>
            Price
          </TableHead>
          <TableHead className={cn('font-semibold', isDark ? 'text-slate-400' : 'text-slate-500')}>
            Status
          </TableHead>
          <TableHead className={cn('font-semibold', isDark ? 'text-slate-400' : 'text-slate-500')}>
            Actions
          </TableHead>
        </TableRow>
      </TableHeader>
      <TableBody>
        {plans.length === 0 ? (
          <TableRow>
            <TableCell colSpan={6} className="text-center py-12 text-slate-500">
              No plans found
            </TableCell>
          </TableRow>
        ) : (
          plans.map((plan) => (
            <TableRow
              key={plan.id}
              className={cn(
                'transition-colors',
                isDark
                  ? 'border-slate-700 hover:bg-slate-800/50'
                  : 'border-slate-100 hover:bg-slate-50',
                !plan.is_active && 'opacity-60',
              )}
            >
              <TableCell className={cn(
                'font-mono text-sm',
                isDark ? 'text-slate-300' : 'text-slate-700'
              )}>{plan.code}</TableCell>
              <TableCell className={cn(
                'font-medium',
                isDark ? 'text-white' : 'text-[#0F172A]'
              )}>{plan.title}</TableCell>
              <TableCell className={cn(
                isDark ? 'text-slate-300' : 'text-slate-700'
              )}>{formatDuration(plan.duration_value, plan.duration_unit)}</TableCell>
              <TableCell>
                <div>
                  <p className={cn(
                    'font-medium',
                    isDark ? 'text-white' : 'text-[#0F172A]'
                  )}>{formatPrice(plan.price)}</p>
                  {plan.old_price && (
                    <p className={cn(
                      'text-xs line-through',
                      isDark ? 'text-slate-500' : 'text-slate-400'
                    )}>{formatPrice(plan.old_price)}</p>
                  )}
                </div>
              </TableCell>
              <TableCell>
                <div className="flex items-center gap-2">
                  <Switch
                    checked={plan.is_active}
                    onCheckedChange={() => onToggleActive(plan)}
                  />
                  <span
                    className={cn(
                      'text-xs font-medium',
                      plan.is_active
                        ? 'text-emerald-500'
                        : isDark
                        ? 'text-slate-500'
                        : 'text-slate-400',
                    )}
                  >
                    {plan.is_active ? 'Active' : 'Inactive'}
                  </span>
                </div>
              </TableCell>
              <TableCell>
                <div className="flex items-center gap-2">
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => onEdit(plan)}
                    className={cn(
                      'h-8 px-2 rounded-lg',
                      isDark
                        ? 'text-slate-400 hover:bg-slate-800 hover:text-white'
                        : 'text-slate-600 hover:bg-slate-100'
                    )}
                  >
                    <Edit className="w-4 h-4" />
                  </Button>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => onDelete(plan.id)}
                    className={cn(
                      'h-8 px-2 rounded-lg',
                      isDark
                        ? 'text-slate-400 hover:bg-slate-800 hover:text-red-400'
                        : 'text-slate-600 hover:bg-slate-100 hover:text-red-600'
                    )}
                  >
                    <Trash2 className="w-4 h-4" />
                  </Button>
                </div>
              </TableCell>
            </TableRow>
          ))
        )}
      </TableBody>
    </Table>
  );
}
